// Copied from frontend/src/components/PowerPills.tsx — the live-power "pills"
// control from the Optimization "Now" card (Home, PV, Battery, Grid chips
// whose translucent fill tracks power vs a max like a horizontal gauge).
// Cloud adaptation: FLOW_COLORS inlined, labels from the sites.* i18n keys,
// and gauge maxes usually absent (0 → no fill, values still shown).
import type { ReactNode } from "react";
// NOT the two-tone set: these icons are tinted per live state (grid red
// when importing, green when exporting; battery by charge direction), and
// a fixed two-colour mark cannot carry that signal.
import { BatteryCharging, Home, PlugZap, Sun } from "lucide-react";
import { useTranslation } from "react-i18next";

const FLOW_COLORS = {
  green: "#22c55e",
  red: "#ef4444",
  blue: "#38bdf8",
  grey: "#64748b",
};
const PV_COLOR = "#eab308";    // solar (matches the local dashboard nodes)
const LOAD_COLOR = "#fb923c";  // home load

function fmtPower(w: number): string {
  return Math.abs(w) >= 1000 ? `${(w / 1000).toFixed(2)} kW` : `${Math.round(w)} W`;
}

export function PowerPill({ color, fillFrac, title, children }: {
  color: string; fillFrac: number; title: string; children: ReactNode;
}) {
  const pct = Math.round(Math.max(0, Math.min(1, fillFrac || 0)) * 100);
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-sm"
      title={title}
      style={{ borderColor: color, background: `linear-gradient(to right, ${color}40 ${pct}%, transparent ${pct}%)` }}>
      {children}
    </span>
  );
}

export interface LivePower {
  pvW: number;
  batW: number;   // + discharging / − charging
  gridW: number;  // + import / − export
  loadW: number;
  socPct?: number | null;
  // Gauge ceilings (W). 0/undefined → uncapped → no fill.
  pvMaxW?: number;
  batMaxChargeW?: number;
  batMaxDischargeW?: number;
  gridMaxChargeW?: number;
  gridMaxDischargeW?: number;
}

// Order: Home, PV, Battery, Grid.
export function LivePowerPills(p: LivePower) {
  const { t } = useTranslation();
  const sz = 15;

  const loadCol = p.loadW < 200 ? FLOW_COLORS.grey : LOAD_COLOR;
  const pvCol = p.pvW < 200 ? FLOW_COLORS.grey : PV_COLOR;
  const charging = p.batW < -200, discharging = p.batW > 200;
  const batCol = charging ? FLOW_COLORS.blue : discharging ? FLOW_COLORS.green : FLOW_COLORS.grey;
  const batMax = (charging ? p.batMaxChargeW : p.batMaxDischargeW) ?? 0;
  const importing = p.gridW > 200, exporting = p.gridW < -200;
  const gridCol = importing ? FLOW_COLORS.red : exporting ? FLOW_COLORS.green : FLOW_COLORS.grey;
  const gridMax = (importing ? p.gridMaxChargeW : p.gridMaxDischargeW) ?? 0;
  const pvMax = p.pvMaxW ?? 0;

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <PowerPill title={t("sites.load")} color={loadCol} fillFrac={0}>
        <Home size={sz} style={{ color: loadCol }} />
        <span className="tabular-nums font-semibold">{fmtPower(p.loadW)}</span>
      </PowerPill>

      <PowerPill title={t("sites.pv")}
        color={pvCol} fillFrac={pvMax > 0 ? p.pvW / pvMax : 0}>
        <Sun size={sz} style={{ color: pvCol }} />
        <span className="tabular-nums font-semibold">{fmtPower(p.pvW)}</span>
      </PowerPill>

      <PowerPill title={t("sites.battery")}
        color={batCol} fillFrac={batMax > 0 ? Math.abs(p.batW) / batMax : 0}>
        <BatteryCharging size={sz} style={{ color: batCol }} />
        {p.socPct != null && <span className="tabular-nums font-semibold">{Math.round(p.socPct)}%</span>}
        {(charging || discharging) && (
          <>
            <span className="tabular-nums font-semibold">{fmtPower(Math.abs(p.batW))}</span>
            <span className="text-xs" style={{ color: batCol }}>
              {charging ? t("sites.charging") : t("sites.discharging")}
            </span>
          </>
        )}
      </PowerPill>

      <PowerPill title={t("sites.grid")}
        color={gridCol} fillFrac={gridMax > 0 ? Math.abs(p.gridW) / gridMax : 0}>
        <PlugZap size={sz} style={{ color: gridCol }} />
        <span className="tabular-nums font-semibold">{fmtPower(Math.abs(p.gridW))}</span>
        {(importing || exporting) && (
          <span className="text-xs" style={{ color: gridCol }}>
            {importing ? t("sites.importing") : t("sites.exporting")}
          </span>
        )}
      </PowerPill>
    </div>
  );
}
